<template>
  <div class="competition-judges">
    <!-- Encabezado con botones -->
    <div class="header-title">
      <h3>{{ $t("judge.invitation.title") }}</h3>
      <div class="buttons-group">
        <button class="toggle-view-btn" @click="toggleViewMode">
          <i :class="viewMode === 'grid' ? 'fa fa-list' : 'fa fa-th-large'"></i>
        </button>
        <button v-if="isOwnerOrganizer(user,competition) && !competition.started_at" class="invite-btn" @click="openInviteModal">
          <i class="fa-solid fa-user-plus"></i> {{ $t("judge.invitation.invite") }}
        </button>
        <button v-if="needDeclinedJudges" class="declined-btn" @click="toggleDeclinedJudges">
          <i class="fa-solid fa-user-slash"></i> {{ showDeclined ? $t("judge.invitation.hide_declined") : $t("judge.invitation.show_declined") }}
        </button>
        <button v-if="needSendMailJudges" class="send-mail-btn" @click="confirmSendEmailToJudges">
          <i class="fa-solid fa-mail-bulk"></i>
          {{ $t("judge.send_mails_judges") }}
        </button>
      </div>
    </div>

    <!-- Control de vista -->
    <template v-if="filteredJudges.length > 0">
      <!-- Vista de tarjetas -->

      <div v-if="viewMode === 'grid'" :class="['judge-grid', filteredJudges.length <= 2 ? 'single' : '']">
        <JudgeCard
            v-for="judge in paginatedJudges"
            :key="judge.id"
            :judge="judge"
            :competition="competition"
            :activeTechOperatorId="activeTechOperatorId"
            @viewJudge="$emit('viewJudge', judge.user_id)"
            @update-show-modal="controlPop"
          @toggle-tech-operator="handleToggleTechOperator"
        />
      </div>

      <!-- Vista de lista -->
      <JudgeTable
          v-if="viewMode === 'list'"
          :judges="filteredJudges"
          :competition="competition"
          :activeTechOperatorId="activeTechOperatorId"
          @viewJudge="handleViewJudge"
          @update-show-modal="controlPop"
          @toggle-tech-operator="handleToggleTechOperator"

      />

      <div class="pagination-controls" v-if="viewMode === 'grid' && totalPages > 1">
        <button class="pagination-btn" @click="currentPage--" :disabled="currentPage === 1">
          {{ $t('athletes.pagination.prev') }}
        </button>

        <span class="pagination-text">
          {{ $t('athletes.pagination.page') }} {{ currentPage }} {{ $t('athletes.pagination.of') }} {{ totalPages }}
        </span>

        <button class="pagination-btn" @click="currentPage++" :disabled="currentPage === totalPages">
          {{ $t('athletes.pagination.next') }}
        </button>
      </div>
    </template>

    <!-- Mensaje si no hay atletas disponibles para invitar -->
    <p v-else class="no-athletes">{{ $t("judge.invitation.empty_athletes") }}</p>

    <!-- Lista de atletas declinados (solo si está activado) -->
    <template v-if="showDeclined && declinedJudges.length > 0">
      <h4>{{ $t("judge.invitation.declined_list") }}</h4>
      <JudgeTable
          :competition="competition"
          :judges="declinedJudges"
      />
    </template>

    <!-- Modal para invitar atletas -->
    <InviteJudgeModal
        :isVisible="isInviteModalVisible"
        :judges="judges_list"
        :competition="competition"
        @closeModal="closeInviteModal"
        @inviteJudges="handleInviteJudeges"
    />
  </div>
  <ConfirmationModal
      ref="confirmationModal"
      :message="modalMessage"
      :showCancel="showCancel"
      @confirmed="confirmModal"
      @canceled="cancelModal"
  />
</template>

<script>
import JudgeCard from "@/components/competitions/JudgeCard.vue";
import InviteJudgeModal from "@/components/competitions/InviteJudgeModal.vue";
import JudgeTable from "@/components/competitions/JudgeTable.vue";
import { isOwnerOrganizer, isOwnerAthlete} from '@/helpers/roleUtils';
import {mapState} from "vuex";
import ConfirmationModal from "@/components/competitions/ConfirmationModal.vue";

export default {
  name: "CompetitionJudges",
  components: { ConfirmationModal, JudgeCard, InviteJudgeModal, JudgeTable },
  props: {
    judges: {
      type: Array,
      required: true,
      default: () => [],
    },
    judges_list: {
      type: Array,
      required: true,
      default: () => [],
    },
    competition: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      isInviteModalVisible: false,
      viewMode: 'grid',
      showDeclined: false,
      counter: 0,
      showCancel: false,
      modalMessage:"",
      pendingAction: null,
      pendingId:null,
      currentPage: 1,
      itemsPerPage: 9,
    };
  },
  computed: {
    ...mapState("competition",["competitionIdToView", "competitionData"]),
    ...mapState('user', ['isAuthenticated','user','token']),
    totalPages() {
      return Math.ceil(this.filteredJudges.length / this.itemsPerPage);
    },
    isOrganizerManager() {
      const hasOrganizerRole = this.user?.roles?.some(r => r.name === 'Organizer' || r.id === 4);
      const hasSupervisorRole = this.user?.roles?.some(r => r.name === 'Supervisor');
      return hasOrganizerRole || hasSupervisorRole || this.isOwnerOrganizer(this.user, this.competition);
    },
    canViewApprovedOnly() {
      return this.user && Array.isArray(this.user.roles) && this.user.roles.some(r => ['Athlete', 'Judge'].includes(r.name));
    },
    filteredJudges() {
      return this.judges.filter(judge => {
        const isJudge = judge.role_id === 2;
        const notDeclined = judge.status_id !== 5;
        if (!isJudge || !notDeclined) return false;

        // Si es organizador, puede ver todos
        if (this.isOrganizerManager) return true;
        // Si es el mismo atleta (usuario actual)
        const isSelf = judge?.user_id === this.user?.id;
        const sendedMail = judge.status_id === 2 || judge.status_id === 3;

        if (isSelf && sendedMail) return true;

        // Si solo puede ver aprobados (judge o athlete), verifica el status
        if (this.canViewApprovedOnly) return judge.status_id === 4;

        return false;
      });
    },
    declinedJudges() {
      return this.judges.filter(judge =>
          judge.role_id === 2 &&
          judge.status_id === 5 &&
          this.isOwnerOrganizer(this.user,this.competition) // solo el organizador creador ve declinados
      );
    },
    needSendMailJudges() {
      return this.judges.some(judge =>
          judge.role_id === 2 &&
          judge.status_id === 1 &&
          this.isOwnerOrganizer(this.user, this.competition)
      );
    },
    needDeclinedJudges() {
      return this.judges.some(judge =>
          judge.role_id === 2 &&
          judge.status_id === 5 &&
          this.isOwnerOrganizer(this.user, this.competition)
      );
    },
    paginatedJudges() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredJudges.slice(start, start + this.itemsPerPage);
    },
    activeTechOperatorId() {
      const active = (this.judges || []).find(judge => {
        const flag = judge?.tech_operator_active;
        return flag === true || flag === 'true' || Number(flag) === 1;
      });
      return active ? active.id : null;
    }
  },
  watch: {
    filteredJudges(newList) {
      const maxPage = Math.max(1, Math.ceil((newList?.length || 0) / this.itemsPerPage));
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage;
      }
    }
  },
  methods: {
    isOwnerAthlete,
    isOwnerOrganizer,
    openInviteModal() {
      this.isInviteModalVisible = true;
    },
    closeInviteModal() {
      this.isInviteModalVisible = false;
    },
    handleInviteJudeges(selectedJudges) {
      this.$emit("inviteJudges", selectedJudges);
    },
    toggleViewMode() {
      this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
    },
    toggleDeclinedJudges() {
      this.showDeclined = !this.showDeclined;
    },
    handleViewJudge(judgeId) {
      this.$emit("viewJudge", judgeId);
    },
    handleRejectJudge(judgeId) {
      this.$emit("rejectJudge", judgeId);
    },
    controlPop(action,id,message){
      this.modalMessage = message;
      this.showCancel = true;
      this.pendingAction = action;
      this.pendingId = id;
      this.$refs.confirmationModal.openModal(); // 🔥 Usa `ref` para abrir el modal
    },
    async confirmModal() {
      switch (this.pendingAction) {
        case 'deleteJudge':
          this.$emit('deleteJudge', this.pendingId);
          break;
        case 'rejectJudge':
          this.$emit('rejectJudge', this.pendingId);
          break;
        case 'confirmJudge':
          this.$emit('confirmJudge', this.pendingId);
          break;
        case 'approveJudge':
          this.$emit('approveJudge', this.pendingId);
          break;
        case 'sendEmailJudge':
          this.$emit('sendEmailToJudge', this.pendingId);
          break;
        case 'sendEmailJudges':
          this.$emit('sendEmailToJudges', this.pendingId);
          break;
        case 'rejectAthlete':
          this.$emit('rejectAthlete', this.pendingId);
          break;
        case 'confirmPayment':
          this.$emit('confirmPayment', this.pendingId);
          break;
          // Agregá más acciones según necesites
        default:
          console.warn("Acción no reconocida:", this.pendingAction);
      }

      // Limpiar los temporales
      this.pendingAction = null;
      this.pendingId = null;
    },
    cancelModal() {
      this.pendingAction = null;
      this.pendingId = null;
      this.selectedCompetitionId = null;
    },
    confirmSendEmailToJudges() {
      if (!this.isOwnerOrganizer(this.user, this.competition)) return;
      this.controlPop(
          'sendEmailJudges',
          this.competition.id,
          this.$t('judge.modal.send_all_invitation_confirmation')
      );
    },
    handleToggleTechOperator(payload) {
      this.$emit('toggleTechOperator', payload);
    },
  }
};
</script>


<style scoped>
.competition-judges {
  margin-top: 20px;
}

.header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.buttons-group {
  display: flex;
  gap: 10px;
}

.toggle-view-btn {
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 18px;
}

.invite-btn {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}

.invite-btn:hover {
  background-color: #0056b3;
}

.declined-btn {
  background-color: rgba(91, 91, 91, 0.99);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}

.send-mail-btn {
  background-color: rgba(134, 112, 65, 0.99);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}
.declined-btn:hover {
  background-color: rgba(91, 91, 91, 0.99);
}
.send-mail-btn:hover {
  background-color: rgba(91, 91, 91, 0.99);
}

.judge-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}
.judge-grid.single {
  justify-content: center;
}

.judge-grid.single .judge-card {
  max-width: 250px;
  width: 100%;
}
.no-athletes {
  text-align: center;
  font-size: 14px;
  color: #777;
}

@media (max-width: 576px) {
  .judge-grid {
    grid-template-columns: repeat(auto-fit, minmax(170px, 2fr));
    gap: 2px;
  }
  .judge-grid .athlete-card {
    max-width: 170px;
  }

  .judge-grid.single .judge-card {
    max-width: 100%;
  }
}

@media (max-width: 768px) {

}
</style>